/**
 * Pure display-state, filter, and sort helpers for the "Decyzje klienta"
 * review table — split out of the presentational components so this logic
 * is unit-testable independent of rendering (mirrors
 * apps/mockups/src/data/client-decisions-filters.ts, adapted to the real
 * UnitDecisionsListItem contract shape instead of mock data).
 */
import type { UnitDecisionsListItem } from '@ooh/contracts';

// ─── Display state ──────────────────────────────────────────────

/**
 * The unit's *display* state — adds SILENT on top of the three raw decision
 * values for the defensive "accepted but zero resolved periods" edge case
 * (client-offer-decision-persistence Scope OUT: rendered defensively here,
 * never produced by this change's own code).
 */
export type UnitDecisionDisplayState = UnitDecisionsListItem['decision'] | 'SILENT';

export function getUnitDisplayState(unit: UnitDecisionsListItem): UnitDecisionDisplayState {
  if (unit.decision === 'ACCEPTED' && (unit.periods == null || unit.periods.length === 0)) {
    return 'SILENT';
  }
  return unit.decision;
}

/** A unit is lockable (can be turned into a reservation) only with real accepted dates. */
export function isLockable(unit: UnitDecisionsListItem): boolean {
  return unit.decision === 'ACCEPTED' && (unit.periods?.length ?? 0) > 0;
}

// ─── Filter / sort ───────────────────────────────────────────────

export type DecisionFilterKey = 'ALL' | 'ACCEPTED' | 'REJECTED' | 'UNDECIDED';
export const UNIT_DECISIONS_SORT_KEYS = [
  'decision',
  'mediaType',
  'format',
  'supplier',
  'supplierNumber',
  'grwId',
  'region',
  'county',
  'agglomeration',
  'city',
  'address',
  'dimensions',
  'audience',
  'notes',
  'price',
] as const;
export type SortKey = (typeof UNIT_DECISIONS_SORT_KEYS)[number];
export type SortDir = 'asc' | 'desc';

const DISPLAY_STATE_RANK: Record<UnitDecisionDisplayState, number> = {
  ACCEPTED: 0,
  SILENT: 1,
  REJECTED: 2,
  UNDECIDED: 3,
};

/** Applies the decision-filter pill + free-text search (address/city/GRW/supplier no.). */
export function filterUnits(
  units: UnitDecisionsListItem[],
  filter: DecisionFilterKey,
  query: string,
): UnitDecisionsListItem[] {
  const q = query.trim().toLowerCase();
  return units.filter((unit) => {
    if (filter !== 'ALL' && unit.decision !== filter) return false;
    if (
      q &&
      !`${unit.address} ${unit.city} ${unit.grwNumber} ${unit.supplierNumber}`
        .toLowerCase()
        .includes(q)
    ) {
      return false;
    }
    return true;
  });
}

function compareNullable<T>(
  a: T | null,
  b: T | null,
  compare: (left: T, right: T) => number,
  dir: number,
): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return compare(a, b) * dir;
}

function compareString(a: string | null, b: string | null, dir: number): number {
  return compareNullable(a, b, (left, right) => left.localeCompare(right, 'pl'), dir);
}

function compareNumber(a: number | null, b: number | null, dir: number): number {
  return compareNullable(a, b, (left, right) => left - right, dir);
}

function compareByKey(
  a: UnitDecisionsListItem,
  b: UnitDecisionsListItem,
  sortKey: SortKey,
  dir: number,
): number {
  if (sortKey === 'decision') {
    return (
      (DISPLAY_STATE_RANK[getUnitDisplayState(a)] - DISPLAY_STATE_RANK[getUnitDisplayState(b)]) *
      dir
    );
  }
  if (sortKey === 'price') return compareNumber(a.offeredPricePln, b.offeredPricePln, dir);
  if (sortKey === 'audience') return compareNumber(a.audienceEstimate, b.audienceEstimate, dir);
  if (sortKey === 'dimensions') {
    return compareNumber(a.widthM, b.widthM, dir) || compareNumber(a.heightM, b.heightM, dir);
  }
  if (sortKey === 'format') return compareString(a.formatCode, b.formatCode, dir);
  return compareString(a[sortKey], b[sortKey], dir);
}

/** Sorts the complete filtered snapshot array before table virtualization. */
export function sortUnits(
  units: UnitDecisionsListItem[],
  sortKey: SortKey,
  sortDir: SortDir,
): UnitDecisionsListItem[] {
  const dir = sortDir === 'asc' ? 1 : -1;
  return [...units].sort((a, b) => {
    return compareByKey(a, b, sortKey, dir) || (a.mediaUnitId - b.mediaUnitId) * dir;
  });
}

// ─── Summary ─────────────────────────────────────────────────────

export interface UnitDecisionsSummary {
  total: number;
  accepted: number;
  rejected: number;
  undecided: number;
  silentCount: number;
  acceptedValuePln: number;
}

/**
 * acceptedValuePln sums only lockable units (ACCEPTED + real accepted dates),
 * excluding SILENT units — matching isLockable()'s definition — since a
 * SILENT unit will never be locked into a reservation and summing its price
 * would overstate the value actually securable from this offer.
 */
export function computeSummary(units: UnitDecisionsListItem[]): UnitDecisionsSummary {
  let accepted = 0;
  let rejected = 0;
  let undecided = 0;
  let silentCount = 0;
  let acceptedValuePln = 0;
  for (const unit of units) {
    if (unit.decision === 'ACCEPTED') accepted += 1;
    else if (unit.decision === 'REJECTED') rejected += 1;
    else undecided += 1;
    if (getUnitDisplayState(unit) === 'SILENT') silentCount += 1;
    if (isLockable(unit)) acceptedValuePln += unit.offeredPricePln ?? 0;
  }
  return { total: units.length, accepted, rejected, undecided, silentCount, acceptedValuePln };
}
